'use client';

import React, { useRef, useEffect, useCallback } from 'react';
import type { SimConfig, RuntimeStats } from './ParticleShowcaseClient';

// ─── Utility ──────────────────────────────────────────────────────────────────
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ─── 3-D formation helpers ────────────────────────────────────────────────────

/** Returns the raw 3-D unit-space position for particle `index` in the given formation.
 *  Coordinates are in [-1, 1] range; caller scales by radius. */
function getFormation3DPosition(
  index: number,
  total: number,
  formation: SimConfig['formation']
): { x: number; y: number; z: number } | null {
  if (formation === 'freeform') return null;

  switch (formation) {
    case 'sphere': {
      // Fibonacci sphere — evenly distributed points on unit sphere surface
      const goldenAngle = Math.PI * (3 - Math.sqrt(5));
      const theta = goldenAngle * index;
      let y = 1 - (index / (total - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      return { x: Math.cos(theta) * r, y, z: Math.sin(theta) * r };
    }
    case 'cube': {
      // Distribute evenly across 6 faces
      const perFace = Math.ceil(total / 6);
      const face = Math.floor(index / perFace);
      const fi = index % perFace;
      const side = Math.ceil(Math.sqrt(perFace));
      const row = Math.floor(fi / side);
      const col = fi % side;
      const u = side > 1 ? (col / (side - 1)) * 2 - 1 : 0;
      const v = side > 1 ? (row / (side - 1)) * 2 - 1 : 0;
      switch (face % 6) {
        case 0: return { x: u,  y: v,  z: 1  };
        case 1: return { x: u,  y: v,  z: -1 };
        case 2: return { x: 1,  y: u,  z: v  };
        case 3: return { x: -1, y: u,  z: v  };
        case 4: return { x: u,  y: 1,  z: v  };
        case 5: return { x: u,  y: -1, z: v  };
        default: return { x: 0, y: 0, z: 0 };
      }
    }
    case 'helix': {
      // Double helix — two strands wound around the Y axis
      const strand = index % 2;
      const t = Math.floor(index / 2) / Math.max(Math.floor(total / 2) - 1, 1);
      const angle = t * Math.PI * 6 + strand * Math.PI; // 3 full turns per strand
      const helixR = 0.55;
      return {
        x: Math.cos(angle) * helixR,
        y: (t - 0.5) * 2,           // -1 to 1
        z: Math.sin(angle) * helixR,
      };
    }
    case 'donut': {
      // Torus — major radius 0.55, tube radius 0.28
      const majorR = 0.55;
      const tubeR  = 0.28;
      const phi   = (index / total) * Math.PI * 2;
      const theta = ((index * 7) % total / total) * Math.PI * 2;
      return {
        x: (majorR + tubeR * Math.cos(theta)) * Math.cos(phi),
        y: tubeR * Math.sin(theta),
        z: (majorR + tubeR * Math.cos(theta)) * Math.sin(phi),
      };
    }
    default:
      return null;
  }
}

/** Rotate a 3-D point around the Y axis by `ry` and X axis by `rx`, then project to 2-D. */
function project3D(
  px: number, py: number, pz: number,
  rx: number, ry: number,
  cx: number, cy: number,
  radius: number
): { sx: number; sy: number; depth: number } {
  // Rotate around Y axis
  const cosY = Math.cos(ry), sinY = Math.sin(ry);
  const x1 = px * cosY + pz * sinY;
  const z1 = -px * sinY + pz * cosY;
  const y1 = py;

  // Rotate around X axis
  const cosX = Math.cos(rx), sinX = Math.sin(rx);
  const y2 = y1 * cosX - z1 * sinX;
  const z2 = y1 * sinX + z1 * cosX;
  const x2 = x1;

  // Perspective projection (mild)
  const fov = 2.8;
  const scale = fov / (fov + z2);

  return {
    sx: cx + x2 * radius * scale,
    sy: cy + y2 * radius * scale,
    depth: z2,   // used for depth-sorting / size scaling
  };
}

// ─── Color Palettes ───────────────────────────────────────────────────────────
export const COLOR_PALETTES: Record<string, string[]> = {
  ocean:    ['#38bdf8', '#0ea5e9', '#7dd3fc', '#bae6fd', '#0284c7', '#e0f2fe'],
  forest:   ['#4ade80', '#22c55e', '#86efac', '#a3e635', '#16a34a', '#bbf7d0'],
  sunset:   ['#fb923c', '#f97316', '#fdba74', '#fbbf24', '#ea580c', '#fed7aa'],
  firefly:  ['#fef08a', '#fde047', '#facc15', '#fbbf24', '#a3e635', '#d9f99d'],
  dusk:     ['#c084fc', '#a855f7', '#e879f9', '#f0abfc', '#818cf8', '#ddd6fe'],
  aurora:   ['#34d399', '#6ee7b7', '#38bdf8', '#a78bfa', '#f472b6', '#86efac'],
  redgreen: ['#ef4444', '#22c55e', '#f87171', '#4ade80', '#dc2626', '#16a34a'],
};

function resolvePalette(colorPalette: string, customColors?: string[]): string[] {
  if (colorPalette === 'custom' && customColors && customColors.length > 0) {
    return customColors;
  }
  return COLOR_PALETTES[colorPalette] ?? COLOR_PALETTES.ocean;
}

interface Particle {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  // 3-D state (used when formation is active)
  x3d: number;
  y3d: number;
  z3d: number;
  vx3d: number;
  vy3d: number;
  vz3d: number;
  size: number;
  baseSize: number;
  color: string;
  bodyColor: string;
  alpha: number;
  life: number;
  maxLife: number;
  angle: number;
  angularVel: number;
  phase: number;
  neighborDist: number;
  rotationSpeed: number;
  wobble: number;
  wobbleSpeed: number;
  glowPhase: number;
  glowSpeed: number;
  tailPhase: number;
  tailSpeed: number;
  // Snowflake
  spinSpeed: number;
  numArms: number;
  // Bubble
  bubbleWobble: number;
  // Star
  numPoints: number;
  twinklePhase: number;
  twinkleSpeed: number;
  // Dragonfly
  wingPhase: number;
  wingSpeed: number;
}

interface DyingParticle {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  alpha: number;
  maxAlpha: number;
  age: number;
  duration: number;
  angle: number;
  spinSpeed: number;
  trail: Array<{ x: number; y: number; a: number }>;
  form: SimConfig['form'];
  pieces: Array<{ x: number; y: number; vx: number; vy: number; size: number; angle: number; spin: number }>;
}

interface Props {
  config: SimConfig;
  onStatsUpdate: (stats: RuntimeStats) => void;
}

// ─── Particle factory ─────────────────────────────────────────────────────────
function createParticle(
  id: string,
  w: number,
  h: number,
  form: SimConfig['form'],
  palette: string[],
  sizeMultiplier: number,
  edge?: 'left' | 'right' | 'top' | 'bottom' | 'random',
  bodyColor?: string
): Particle {
  const color = palette[Math.floor(id.charCodeAt(id.length - 1) % palette.length)];
  const resolvedBodyColor = bodyColor && bodyColor.length > 0 ? bodyColor : color;

  let x = Math.random() * w;
  let y = Math.random() * h;
  let vx = 0;
  let vy = 0;

  if (edge === 'left')   { x = -10; y = Math.random() * h; }
  if (edge === 'right')  { x = w + 10; y = Math.random() * h; }
  if (edge === 'top')    { x = Math.random() * w; y = -10; }
  if (edge === 'bottom') { x = Math.random() * w; y = h + 10; }

  const baseSpeed = 0.6 + Math.random() * 0.8;

  switch (form) {
    case 'fish':
      vx = baseSpeed * (Math.random() > 0.5 ? 1 : -1);
      vy = (Math.random() - 0.5) * 0.4;
      break;
    case 'butterfly':
      vx = (Math.random() - 0.5) * baseSpeed;
      vy = (Math.random() - 0.5) * baseSpeed;
      break;
    case 'birds':
      vx = baseSpeed * 0.8 * (Math.random() > 0.3 ? 1 : -1);
      vy = (Math.random() - 0.5) * 0.5;
      break;
    case 'fireflies':
      vx = (Math.random() - 0.5) * 0.3;
      vy = (Math.random() - 0.5) * 0.3;
      break;
    case 'leaves':
      vx = (Math.random() - 0.5) * 0.8;
      vy = 0.4 + Math.random() * 0.6;
      break;
    case 'dragonfly':
      vx = (Math.random() - 0.5) * baseSpeed * 1.2;
      vy = (Math.random() - 0.5) * baseSpeed * 0.8;
      break;
    case 'snowflake':
      vx = (Math.random() - 0.5) * 0.4;
      vy = 0.3 + Math.random() * 0.5;
      break;
    case 'bubble':
      vx = (Math.random() - 0.5) * 0.3;
      vy = -(0.3 + Math.random() * 0.5);
      break;
    case 'star':
      vx = (Math.random() - 0.5) * 0.1;
      vy = (Math.random() - 0.5) * 0.1;
      break;
  }

  const baseSizeMap: Record<string, number> = {
    fireflies: 3, birds: 4, fish: 5, butterfly: 6, leaves: 5,
    dragonfly: 5, snowflake: 6, bubble: 8, star: 4,
  };
  const baseSize = baseSizeMap[form] ?? 5;

  return {
    id,
    x, y, vx, vy,
    x3d: (Math.random() - 0.5) * 2,
    y3d: (Math.random() - 0.5) * 2,
    z3d: (Math.random() - 0.5) * 2,
    vx3d: 0, vy3d: 0, vz3d: 0,
    size: (baseSize + Math.random() * 2) * sizeMultiplier,
    baseSize: (baseSize + Math.random() * 2),
    color,
    bodyColor: resolvedBodyColor,
    alpha: 0.7 + Math.random() * 0.3,
    life: Math.random(),
    maxLife: 0.8 + Math.random() * 0.4,
    angle: Math.atan2(vy, vx),
    angularVel: (Math.random() - 0.5) * 0.02,
    phase: Math.random() * Math.PI * 2,
    neighborDist: 60 + Math.random() * 40,
    rotationSpeed: (Math.random() - 0.5) * 0.04,
    wobble: 0,
    wobbleSpeed: 0.02 + Math.random() * 0.03,
    glowPhase: Math.random() * Math.PI * 2,
    glowSpeed: 0.015 + Math.random() * 0.025,
    tailPhase: Math.random() * Math.PI * 2,
    tailSpeed: 0.08 + Math.random() * 0.06,
    spinSpeed: (Math.random() - 0.5) * 0.03,
    numArms: [6, 8][Math.floor(Math.random() * 2)],
    bubbleWobble: Math.random() * Math.PI * 2,
    numPoints: [4, 5, 6][Math.floor(Math.random() * 3)],
    twinklePhase: Math.random() * Math.PI * 2,
    twinkleSpeed: 0.03 + Math.random() * 0.05,
    wingPhase: Math.random() * Math.PI * 2,
    wingSpeed: 0.12 + Math.random() * 0.08,
  };
}

// ─── Drawing functions ────────────────────────────────────────────────────────

function drawFish(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  ctx.save();
  ctx.translate(p.x, p.y);
  const dir = Math.atan2(p.vy, p.vx);
  ctx.rotate(dir);

  const tailWag = Math.sin(p.tailPhase + t * p.tailSpeed * 60) * 0.3;
  const s = p.size;

  ctx.globalAlpha = p.alpha;

  ctx.beginPath();
  ctx.ellipse(0, 0, s, s * 0.45, 0, 0, Math.PI * 2);
  ctx.fillStyle = p.bodyColor;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(-s * 0.85, 0);
  ctx.lineTo(-s * 1.6, -s * 0.55 + tailWag * s);
  ctx.lineTo(-s * 1.6,  s * 0.55 + tailWag * s);
  ctx.closePath();
  ctx.fillStyle = p.color;
  ctx.globalAlpha = p.alpha * 0.8;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(s * 0.45, -s * 0.12, s * 0.12, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.globalAlpha = p.alpha;
  ctx.fill();

  ctx.restore();
}

function drawButterfly(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  ctx.save();
  ctx.translate(p.x, p.y);
  const flapT = (Math.sin(p.phase + t * 3.5) + 1) / 2;
  const wingSpread = 1 - flapT * 0.75;
  const s = p.size;
  ctx.globalAlpha = p.alpha;

  const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, s * 1.4);
  grad.addColorStop(0, p.color);
  grad.addColorStop(0.6, p.color + 'cc');
  grad.addColorStop(1, p.color + '44');
  ctx.fillStyle = grad;

  ctx.beginPath();
  ctx.moveTo(0, -s * 0.1);
  ctx.bezierCurveTo(-s * 0.3 * wingSpread, -s * 1.1, -s * 1.3 * wingSpread, -s * 0.9, -s * 1.2 * wingSpread, s * 0.1);
  ctx.bezierCurveTo(-s * 1.1 * wingSpread, s * 0.4, -s * 0.2 * wingSpread, s * 0.2, 0, s * 0.1);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(0, -s * 0.1);
  ctx.bezierCurveTo(s * 0.3 * wingSpread, -s * 1.1, s * 1.3 * wingSpread, -s * 0.9, s * 1.2 * wingSpread, s * 0.1);
  ctx.bezierCurveTo(s * 1.1 * wingSpread, s * 0.4, s * 0.2 * wingSpread, s * 0.2, 0, s * 0.1);
  ctx.closePath();
  ctx.fill();

  ctx.globalAlpha = p.alpha * 0.85;
  ctx.beginPath();
  ctx.moveTo(0, s * 0.1);
  ctx.bezierCurveTo(-s * 0.2 * wingSpread, s * 0.3, -s * 0.9 * wingSpread, s * 1.0, -s * 0.7 * wingSpread, s * 0.7);
  ctx.bezierCurveTo(-s * 0.5 * wingSpread, s * 0.5, -s * 0.1 * wingSpread, s * 0.4, 0, s * 0.2);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(0, s * 0.1);
  ctx.bezierCurveTo(s * 0.2 * wingSpread, s * 0.3, s * 0.9 * wingSpread, s * 1.0, s * 0.7 * wingSpread, s * 0.7);
  ctx.bezierCurveTo(s * 0.5 * wingSpread, s * 0.5, s * 0.1 * wingSpread, s * 0.4, 0, s * 0.2);
  ctx.closePath();
  ctx.fill();

  ctx.globalAlpha = p.alpha;
  ctx.beginPath();
  ctx.ellipse(0, 0, s * 0.1, s * 0.65, 0, 0, Math.PI * 2);
  ctx.fillStyle = p.bodyColor;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, 0, s * 0.1, 0, Math.PI * 2);
  ctx.fillStyle = p.bodyColor;
  ctx.fill();

  ctx.strokeStyle = 'rgba(20,10,5,0.6)';
  ctx.lineWidth = s * 0.06;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.55);
  ctx.quadraticCurveTo(-s * 0.25 * wingSpread, -s * 1.1, -s * 0.35 * wingSpread, -s * 1.25);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-s * 0.35 * wingSpread, -s * 1.28, s * 0.07, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(20,10,5,0.6)';
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(0, -s * 0.55);
  ctx.quadraticCurveTo(s * 0.25 * wingSpread, -s * 1.1, s * 0.35 * wingSpread, -s * 1.25);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(s * 0.35 * wingSpread, -s * 1.28, s * 0.07, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(20,10,5,0.6)';
  ctx.fill();

  ctx.restore();
}

function drawBird(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  ctx.save();
  ctx.translate(p.x, p.y);
  const dir = Math.atan2(p.vy, p.vx);
  ctx.rotate(dir);

  const flapT = Math.sin(p.phase + t * 4.5);
  const wingUp = flapT * 0.55;
  const s = p.size;
  ctx.globalAlpha = p.alpha;
  ctx.fillStyle = p.bodyColor;

  ctx.beginPath();
  ctx.ellipse(0, 0, s * 0.9, s * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.arc(s * 0.85, -s * 0.08, s * 0.22, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(s * 1.05, -s * 0.1);
  ctx.lineTo(s * 1.45, -s * 0.05);
  ctx.lineTo(s * 1.05, s * 0.0);
  ctx.closePath();
  ctx.fillStyle = p.bodyColor + 'cc';
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(-s * 0.85, 0);
  ctx.lineTo(-s * 1.4, -s * 0.22);
  ctx.lineTo(-s * 1.25, s * 0.05);
  ctx.lineTo(-s * 1.4, s * 0.22);
  ctx.closePath();
  ctx.fillStyle = p.bodyColor;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(-s * 0.1, 0);
  ctx.bezierCurveTo(-s * 0.1, s * 0.15 - wingUp * s * 0.3, -s * 0.7, s * 0.6 - wingUp * s * 0.9, -s * 0.2, s * 0.55 - wingUp * s * 1.1);
  ctx.bezierCurveTo(s * 0.3, s * 0.35 - wingUp * s * 0.6, s * 0.4, s * 0.15 - wingUp * s * 0.2, s * 0.5, -s * 0.05);
  ctx.closePath();
  ctx.fillStyle = p.bodyColor + 'dd';
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(s * 0.1, 0);
  ctx.bezierCurveTo(s * 0.1, -s * 0.15 + wingUp * s * 0.3, s * 0.7, -s * 0.6 + wingUp * s * 0.9, s * 0.2, -s * 0.55 + wingUp * s * 1.1);
  ctx.bezierCurveTo(s * 0.8, s * 0.1, s * 0.3, s * 0.05, s * 0.1, s * 0.05);
  ctx.closePath();
  ctx.fillStyle = p.bodyColor + 'dd';
  ctx.fill();

  ctx.restore();
}

function drawFirefly(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  ctx.save();
  ctx.translate(p.x, p.y);

  const glow = (Math.sin(p.glowPhase + t * p.glowSpeed * 60) + 1) / 2;
  const s = p.size;
  const dir = Math.atan2(p.vy || 0.001, p.vx || 0.001);
  ctx.rotate(dir);

  const haloR = s * 5;
  const haloGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, haloR);
  haloGrad.addColorStop(0, p.color + Math.round(glow * 0x99).toString(16).padStart(2, '0'));
  haloGrad.addColorStop(0.5, p.color + Math.round(glow * 0x44).toString(16).padStart(2, '0'));
  haloGrad.addColorStop(1, p.color + '00');
  ctx.beginPath();
  ctx.arc(0, 0, haloR, 0, Math.PI * 2);
  ctx.fillStyle = haloGrad;
  ctx.globalAlpha = p.alpha * (0.3 + glow * 0.5);
  ctx.fill();

  const wingAlpha = 0.18 + glow * 0.12;
  ctx.globalAlpha = p.alpha * wingAlpha;
  ctx.fillStyle = p.color + 'aa';

  ctx.beginPath();
  ctx.moveTo(-s * 0.05, -s * 0.1);
  ctx.bezierCurveTo(-s * 0.4, -s * 0.9, -s * 1.2, -s * 0.7, -s * 1.0, -s * 0.1);
  ctx.bezierCurveTo(-s * 0.8, s * 0.1, -s * 0.3, s * 0.05, -s * 0.05, 0);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(s * 0.05, -s * 0.1);
  ctx.bezierCurveTo(s * 0.4, -s * 0.9, s * 1.2, -s * 0.7, s * 1.0, -s * 0.1);
  ctx.bezierCurveTo(s * 0.8, s * 0.1, s * 0.3, s * 0.05, s * 0.05, 0);
  ctx.closePath();
  ctx.fill();

  ctx.globalAlpha = p.alpha * 0.9;
  ctx.fillStyle = 'rgba(30,20,5,0.9)';
  ctx.beginPath();
  ctx.arc(0, -s * 0.5, s * 0.16, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-s * 0.08, -s * 0.54, s * 0.05, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(s * 0.08, -s * 0.54, s * 0.05, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = 'rgba(20,15,5,0.7)';
  ctx.lineWidth = s * 0.05;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-s * 0.06, -s * 0.62);
  ctx.quadraticCurveTo(-s * 0.2, -s * 0.95, -s * 0.28, -s * 1.05);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(s * 0.06, -s * 0.62);
  ctx.quadraticCurveTo(s * 0.2, -s * 0.95, s * 0.28, -s * 1.05);
  ctx.stroke();

  ctx.restore();
}

function drawLeaf(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  ctx.save();
  ctx.translate(p.x, p.y);
  const wobble = Math.sin(p.wobble) * 0.4;
  ctx.rotate(p.angle + wobble);
  const s = p.size;
  ctx.globalAlpha = p.alpha;

  const grad = ctx.createLinearGradient(-s, 0, s, 0);
  grad.addColorStop(0, p.color + 'aa');
  grad.addColorStop(0.5, p.color);
  grad.addColorStop(1, p.color + 'aa');

  ctx.beginPath();
  ctx.moveTo(0, -s * 0.9);
  ctx.bezierCurveTo(s * 0.7, -s * 0.5, s * 0.7, s * 0.5, 0, s * 0.9);
  ctx.bezierCurveTo(-s * 0.7, s * 0.5, -s * 0.7, -s * 0.5, 0, -s * 0.9);
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(0, -s * 0.85);
  ctx.lineTo(0, s * 0.85);
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 0.8;
  ctx.stroke();

  ctx.restore();
}

function drawDragonfly(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  ctx.save();
  ctx.translate(p.x, p.y);
  const dir = Math.atan2(p.vy || 0.001, p.vx || 0.001);
  ctx.rotate(dir);

  const flapT = Math.sin(p.wingPhase + t * p.wingSpeed * 60);
  const wingY = flapT * 0.3;
  const s = p.size;
  ctx.globalAlpha = p.alpha;

  const segCount = 6;
  for (let i = 0; i < segCount; i++) {
    const segX = -s * 0.4 - i * s * 0.38;
    const segW = s * 0.22 - i * s * 0.02;
    const segH = s * 0.12 - i * s * 0.01;
    const alpha = 1 - i * 0.05;
    ctx.globalAlpha = p.alpha * alpha;
    ctx.fillStyle = i % 2 === 0 ? p.bodyColor : p.bodyColor + 'cc';
    ctx.beginPath();
    ctx.ellipse(segX, 0, segW, segH, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalAlpha = p.alpha;
  ctx.fillStyle = p.bodyColor;
  ctx.beginPath();
  ctx.ellipse(0, 0, s * 0.32, s * 0.2, 0, 0, Math.PI * 2);
  ctx.fill();

  const wingColors = [p.color + '55', p.color + '44'];

  ctx.globalAlpha = p.alpha * 0.7;
  ctx.fillStyle = wingColors[0];
  ctx.beginPath();
  ctx.moveTo(s * 0.1, -s * 0.05);
  ctx.bezierCurveTo(s * 0.2, -s * 0.45 - wingY * s * 0.8, s * 1.1, -s * 0.75 - wingY * s * 0.8, s * 1.0, -s * 0.1);
  ctx.bezierCurveTo(s * 0.8, s * 0.1, s * 0.1, s * 0.05, -s * 0.1, -s * 0.05);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(s * 0.1, s * 0.05);
  ctx.bezierCurveTo(s * 0.2, s * 0.45 + wingY * s * 0.8, s * 1.1, s * 0.75 + wingY * s * 0.8, s * 1.0, s * 0.1);
  ctx.bezierCurveTo(s * 0.8, -s * 0.1, s * 0.1, -s * 0.05, -s * 0.1, s * 0.05);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = wingColors[1];
  ctx.beginPath();
  ctx.moveTo(-s * 0.1, -s * 0.05);
  ctx.bezierCurveTo(-s * 0.1, -s * 0.45 - wingY * s * 0.8, s * 1.1, -s * 0.75 - wingY * s * 0.8, s * 1.0, -s * 0.1);
  ctx.bezierCurveTo(s * 0.8, s * 0.1, s * 0.1, s * 0.05, -s * 0.1, -s * 0.05);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(-s * 0.1, s * 0.05);
  ctx.bezierCurveTo(-s * 0.1, s * 0.45 + wingY * s * 0.8, s * 1.1, s * 0.75 + wingY * s * 0.8, s * 1.0, s * 0.1);
  ctx.bezierCurveTo(s * 0.8, -s * 0.1, s * 0.1, -s * 0.05, -s * 0.1, s * 0.05);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.globalAlpha = p.alpha;
  ctx.fillStyle = p.color;
  ctx.beginPath();
  ctx.arc(s * 0.45, 0, s * 0.22, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.beginPath();
  ctx.arc(s * 0.52, -s * 0.12, s * 0.12, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(s * 0.52, s * 0.12, s * 0.12, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawSnowflake(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle + t * p.spinSpeed * 30);

  const s = p.size;
  ctx.globalAlpha = p.alpha * 0.9;
  ctx.strokeStyle = p.color;
  ctx.lineCap = 'round';

  const arms = p.numArms;
  const angleStep = (Math.PI * 2) / arms;

  for (let i = 0; i < arms; i++) {
    ctx.save();
    ctx.rotate(i * angleStep);

    ctx.lineWidth = s * 0.12;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -s);
    ctx.stroke();

    const branchPositions = [0.35, 0.65];
    for (const pos of branchPositions) {
      const branchLen = s * (0.35 - pos * 0.15);
      ctx.lineWidth = s * 0.07;
      ctx.beginPath();
      ctx.moveTo(0, -s * pos);
      ctx.lineTo(-branchLen * 0.7, -s * pos - branchLen * 0.7);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, -s * pos);
      ctx.lineTo(branchLen * 0.7, -s * pos - branchLen * 0.7);
      ctx.stroke();
    }

    ctx.restore();
  }

  ctx.beginPath();
  ctx.arc(0, 0, s * 0.15, 0, Math.PI * 2);
  ctx.fillStyle = p.color;
  ctx.fill();

  ctx.restore();
}

function drawBubble(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  const wobble = Math.sin(p.bubbleWobble + t * 1.5) * p.size * 0.08;
  const r = p.size + wobble;

  const ringGrad = ctx.createRadialGradient(p.x, p.y, r * 0.7, p.x, p.y, r);
  ringGrad.addColorStop(0, p.color + '00');
  ringGrad.addColorStop(0.7, p.color + '33');
  ringGrad.addColorStop(1, p.color + '88');
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.fillStyle = ringGrad;
  ctx.globalAlpha = p.alpha;
  ctx.fill();

  const innerGrad = ctx.createRadialGradient(p.x - r * 0.3, p.y - r * 0.3, 0, p.x, p.y, r);
  innerGrad.addColorStop(0, 'rgba(255,255,255,0.12)');
  innerGrad.addColorStop(0.5, p.color + '08');
  innerGrad.addColorStop(1, p.color + '00');
  ctx.beginPath();
  ctx.arc(p.x, p.y, r * 0.95, 0, Math.PI * 2);
  ctx.fillStyle = innerGrad;
  ctx.globalAlpha = p.alpha * 0.8;
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(p.x - r * 0.28, p.y - r * 0.3, r * 0.22, r * 0.14, -Math.PI / 4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.globalAlpha = p.alpha * 0.7;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(p.x + r * 0.3, p.y + r * 0.25, r * 0.07, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.globalAlpha = p.alpha * 0.5;
  ctx.fill();
}

function drawStar(ctx: CanvasRenderingContext2D, p: Particle, t: number) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle + t * p.twinkleSpeed * 60);

  const twinkle = (Math.sin(p.twinklePhase + t * p.twinkleSpeed * 60) + 1) / 2;
  const s = p.size * (0.7 + twinkle * 0.5);
  const n = p.numPoints;
  const innerR = s * 0.4;

  const glowGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, s * 2.5);
  glowGrad.addColorStop(0, p.color + Math.round(twinkle * 0x88).toString(16).padStart(2, '0'));
  glowGrad.addColorStop(1, p.color + '00');
  ctx.beginPath();
  ctx.arc(0, 0, s * 2.5, 0, Math.PI * 2);
  ctx.fillStyle = glowGrad;
  ctx.globalAlpha = p.alpha * twinkle * 0.6;
  ctx.fill();

  ctx.globalAlpha = p.alpha * (0.6 + twinkle * 0.4);
  ctx.fillStyle = p.color;
  ctx.beginPath();
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? s : innerR;
    const angle = (i * Math.PI) / n - Math.PI / 2;
    if (i === 0) ctx.moveTo(Math.cos(angle) * r, Math.sin(angle) * r);
    else ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
  }
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, 0, s * 0.25, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.globalAlpha = p.alpha * twinkle * 0.9;
  ctx.fill();

  ctx.restore();
}

// ─── Dying particle drawing ───────────────────────────────────────────────────

function drawDyingParticle(ctx: CanvasRenderingContext2D, dp: DyingParticle) {
  const progress = dp.age;
  const fadeAlpha = dp.maxAlpha * (1 - progress);

  ctx.save();
  for (const piece of dp.pieces) {
    const pAlpha = fadeAlpha * (0.9 - progress * 0.5);
    if (pAlpha <= 0) continue;
    ctx.save();
    ctx.translate(piece.x, piece.y);
    ctx.rotate(piece.angle);
    ctx.globalAlpha = pAlpha;
    ctx.fillStyle = dp.color;
    const s = piece.size * (1 - progress * 0.6);
    if (s <= 0) { ctx.restore(); continue; }
    ctx.beginPath();
    ctx.rect(-s * 0.5, -s * 0.5, s, s);
    ctx.fill();
    ctx.restore();
  }
  ctx.restore();
}

// ─── Main canvas component ────────────────────────────────────────────────────

export default function ParticleCanvas({ config, onStatsUpdate }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const dyingParticlesRef = useRef<DyingParticle[]>([]);
  const animFrameRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const fpsRef = useRef<number>(60);
  const frameCountRef = useRef<number>(0);
  const fpsTimerRef = useRef<number>(0);
  const mouseRef = useRef<{ x: number; y: number; down: boolean }>({ x: 0, y: 0, down: false });
  const configRef = useRef(config);
  const particleIdCounter = useRef(0);
  // Rotation angles for 3-D formations (auto-rotate over time)
  const rotationRef = useRef<{ ry: number; rx: number }>({ ry: 0, rx: 0.35 });
  // Left-click drag state for manual 3D rotation
  const leftDragRef = useRef<{ active: boolean; lastX: number; lastY: number }>({ active: false, lastX: 0, lastY: 0 });
  // Right-click destroy state
  const rightDestroyRef = useRef<boolean>(false);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  const buildParticles = useCallback((w: number, h: number, cfg: SimConfig) => {
    const palette = resolvePalette(cfg.colorPalette, cfg.customColors);
    const count = cfg.density;
    const particles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      const id = `p-${++particleIdCounter.current}`;
      particles.push(createParticle(id, w, h, cfg.form, palette, cfg.particleSize, undefined, cfg.bodyColor));
    }
    return particles;
  }, []);

  const animate = useCallback((timestamp: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dt = Math.min((timestamp - lastTimeRef.current) / 16.67, 3);
    lastTimeRef.current = timestamp;

    frameCountRef.current++;
    fpsTimerRef.current += timestamp - (lastTimeRef.current - dt * 16.67);
    if (frameCountRef.current % 30 === 0) {
      fpsRef.current = Math.round(1000 / ((timestamp - fpsTimerRef.current / 30) || 16.67));
      fpsRef.current = Math.min(Math.max(fpsRef.current, 1), 120);
      fpsTimerRef.current = 0;
    }

    const cfg = configRef.current;
    const palette = resolvePalette(cfg.colorPalette, cfg.customColors);
    const w = canvas.width;
    const h = canvas.height;
    const t = timestamp * 0.001;
    const speedMult = cfg.speed;
    const mouse = mouseRef.current;

    // Build trail fill from configured bg color
    if (cfg.bgType === 'image' || cfg.bgType === 'none') {
      // Use destination-out to fade previous frame pixels, creating trail effect
      // while keeping canvas transparent so the CSS background image shows through
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';
    } else {
      const trailColor = hexToRgba(cfg.bgColor, 0.18);
      ctx.fillStyle = trailColor;
      ctx.fillRect(0, 0, w, h);
    }

    // ── Advance formation rotation ──────────────────────────────────────────
    const isFormation = cfg.formation !== 'freeform';
    if (isFormation) {
      // Only auto-rotate when not being manually dragged
      if (!leftDragRef.current.active) {
        rotationRef.current.ry += 0.004 * dt;  // slow Y-axis spin
        rotationRef.current.rx = 0.35 + Math.sin(t * 0.18) * 0.22; // gentle X tilt oscillation
      }
    }

    const particles = particlesRef.current;
    const toRemove = new Set<string>();

    if (rightDestroyRef.current) {
      for (const p of particles) {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        if (dx * dx + dy * dy < 1600) {
          toRemove.add(p.id);

          // Spawn small pieces flying apart
          const numPieces = 5 + Math.floor(Math.random() * 5);
          const pieces: DyingParticle['pieces'] = [];
          for (let pi = 0; pi < numPieces; pi++) {
            const pieceAngle = Math.random() * Math.PI * 2;
            const pieceSpeed = 0.6 + Math.random() * 1.2;
            pieces.push({
              x: p.x + (Math.random() - 0.5) * p.size,
              y: p.y + (Math.random() - 0.5) * p.size,
              vx: Math.cos(pieceAngle) * pieceSpeed,
              vy: Math.sin(pieceAngle) * pieceSpeed - 0.3,
              size: p.size * (0.15 + Math.random() * 0.25),
              angle: Math.random() * Math.PI * 2,
              spin: (Math.random() - 0.5) * 0.25,
            });
          }

          dyingParticlesRef.current.push({
            id: `d-${p.id}`,
            x: p.x,
            y: p.y,
            vx: 0,
            vy: 0,
            size: p.size,
            color: p.color,
            alpha: p.alpha,
            maxAlpha: p.alpha,
            age: 0,
            duration: 30 + Math.random() * 20,
            angle: p.angle,
            spinSpeed: 0,
            trail: [],
            form: cfg.form,
            pieces,
          });
        }
      }
    }

    let centerX = 0, centerY = 0, avgVx = 0, avgVy = 0;
    if (cfg.form === 'birds' && particles.length > 0) {
      for (const p of particles) { centerX += p.x; centerY += p.y; avgVx += p.vx; avgVy += p.vy; }
      centerX /= particles.length; centerY /= particles.length;
      avgVx /= particles.length; avgVy /= particles.length;
    }

    const cx = w * 0.5;
    const cy = h * 0.5;
    const radius = Math.min(cx, cy) * 0.58;
    const { ry, rx } = rotationRef.current;

    // Pre-compute 3-D targets for all particles (formation mode)
    // We store them indexed by particle array position
    const targets3D: Array<{ sx: number; sy: number; depth: number } | null> = [];
    if (isFormation) {
      for (let i = 0; i < particles.length; i++) {
        const pos = getFormation3DPosition(i, particles.length, cfg.formation);
        if (pos) {
          targets3D.push(project3D(pos.x, pos.y, pos.z, rx, ry, cx, cy, radius));
        } else {
          targets3D.push(null);
        }
      }
    }

    for (let pi = 0; pi < particles.length; pi++) {
      const p = particles[pi];
      if (toRemove.has(p.id)) continue;

      p.life += 0.003 * dt;
      p.phase += 0.05 * dt;
      p.tailPhase += p.tailSpeed * dt;
      p.glowPhase += p.glowSpeed * dt;
      p.wobble += p.wobbleSpeed * dt;
      p.wingPhase += p.wingSpeed * dt;
      p.twinklePhase += p.twinkleSpeed * dt;
      p.bubbleWobble += 0.04 * dt;

      if (isFormation) {
        // ── 3-D formation mode ──────────────────────────────────────────────
        // In formation mode particles move in 3-D space toward their target,
        // then we project to screen. Natural animation (wobble, phase) still runs.

        const target = targets3D[pi];
        if (target) {
          // Attract toward projected screen target
          const dx = target.sx - p.x;
          const dy = target.sy - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const strength = dist > 60 ? 0.022 : dist > 15 ? 0.010 : 0.004;
          p.vx += dx * strength * dt;
          p.vy += dy * strength * dt;

          // Add gentle organic drift so particles feel alive within the formation
          p.vx += (Math.random() - 0.5) * 0.04 * speedMult;
          p.vy += (Math.random() - 0.5) * 0.04 * speedMult;

          // Dampen — tighter when close to target
          const damp = dist > 60 ? 0.88 : 0.84;
          p.vx *= damp;
          p.vy *= damp;

          p.x += p.vx * dt;
          p.y += p.vy * dt;

          // Keep size consistent — use baseSize scaled by current sizeMultiplier
          p.size = p.baseSize * cfg.particleSize;
        }
      } else {
        // ── Freeform: original per-form movement ────────────────────────────
        switch (cfg.form) {
          case 'fish': {
            const wag = Math.sin(p.tailPhase) * 0.015;
            p.vy += wag;
            p.vy *= 0.92;
            p.vx *= 0.999;
            const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
            const maxSpd = 1.2 * speedMult;
            const minSpd = 0.5 * speedMult;
            if (spd > maxSpd) { p.vx = (p.vx / spd) * maxSpd; p.vy = (p.vy / spd) * maxSpd; }
            // Soft horizontal steering: nudge fish away from left/right edges
            const margin = 80;
            if (p.x < margin)     p.vx += (margin - p.x) * 0.006 * speedMult;
            if (p.x > w - margin) p.vx -= (p.x - (w - margin)) * 0.006 * speedMult;
            // Enforce minimum forward speed so fish always swim
            const absVx = Math.abs(p.vx);
            if (absVx < minSpd) {
              const dir = p.vx >= 0 ? 1 : -1;
              p.vx = dir * minSpd;
            }
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            // Wrap vertically so fish re-enter from the opposite side
            if (p.y < -20) p.y = h + 20;
            if (p.y > h + 20) p.y = -20;
            // Safety clamp for horizontal (steering should prevent reaching here)
            if (p.x < -10) { p.x = -10; p.vx = Math.abs(p.vx); }
            if (p.x > w + 10) { p.x = w + 10; p.vx = -Math.abs(p.vx); }
            break;
          }
          case 'butterfly': {
            // Gentle wandering: steer with small random forces instead of resetting velocity
            p.vx += (Math.random() - 0.5) * 0.12;
            p.vy += (Math.random() - 0.5) * 0.12;
            // Soft vertical flutter using phase
            p.vy += Math.sin(p.phase * 0.4) * 0.006;
            p.vx *= 0.975;
            p.vy *= 0.975;
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            if (p.x < -20) p.x = w + 20;
            if (p.x > w + 20) p.x = -20;
            if (p.y > h + 20) { p.y = -20; p.x = Math.random() * w; }
            break;
          }
          case 'birds': {
            // ── Boid flocking: separation, alignment, cohesion ──
            let sepX = 0, sepY = 0;
            let alignVx = 0, alignVy = 0;
            let cohX = 0, cohY = 0;
            let sepCount = 0, neighborCount = 0;

            for (const other of particles) {
              if (other.id === p.id) continue;
              const dx = p.x - other.x;
              const dy = p.y - other.y;
              const d2 = dx * dx + dy * dy;
              // Separation: avoid very close neighbors (radius ~40px)
              if (d2 < 1600 && d2 > 0) {
                const d = Math.sqrt(d2);
                sepX += (dx / d) * (40 - d) / 40;
                sepY += (dy / d) * (40 - d) / 40;
                sepCount++;
              }
              // Alignment + cohesion: look at neighbors within ~120px
              if (d2 < 14400 && d2 > 0) {
                alignVx += other.vx;
                alignVy += other.vy;
                cohX += other.x;
                cohY += other.y;
                neighborCount++;
              }
            }

            // Separation
            if (sepCount > 0) {
              p.vx += sepX * 0.06 * speedMult;
              p.vy += sepY * 0.06 * speedMult;
            }
            // Alignment — steer toward average heading
            if (neighborCount > 0) {
              const avx = alignVx / neighborCount;
              const avy = alignVy / neighborCount;
              p.vx += (avx - p.vx) * 0.04 * speedMult;
              p.vy += (avy - p.vy) * 0.04 * speedMult;
              // Cohesion — steer toward center of neighbors
              const ncx = cohX / neighborCount;
              const ncy = cohY / neighborCount;
              p.vx += (ncx - p.x) * 0.0008 * speedMult;
              p.vy += (ncy - p.y) * 0.0008 * speedMult;
            }

            // Gentle wing-flap undulation
            p.vy += Math.sin(p.phase * 1.2) * 0.008;

            // Clamp speed
            const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
            const maxSpd = 2.2 * speedMult;
            const minSpd = 0.6 * speedMult;
            if (spd > maxSpd) { p.vx = (p.vx / spd) * maxSpd; p.vy = (p.vy / spd) * maxSpd; }
            else if (spd < minSpd && spd > 0.01) { p.vx = (p.vx / spd) * minSpd; p.vy = (p.vy / spd) * minSpd; }

            p.x += p.vx * dt;
            p.y += p.vy * dt;

            // Soft edge steering — nudge birds away from borders instead of hard bounce
            const margin = 80;
            if (p.x < margin)       p.vx += (margin - p.x) * 0.004 * speedMult;
            if (p.x > w - margin)   p.vx -= (p.x - (w - margin)) * 0.004 * speedMult;
            if (p.y < margin)       p.vy += (margin - p.y) * 0.004 * speedMult;
            if (p.y > h - margin)   p.vy -= (p.y - (h - margin)) * 0.004 * speedMult;

            // Hard clamp to canvas (safety)
            if (p.x < 0) p.x = 0;
            if (p.x > w) p.x = w;
            if (p.y < 0) p.y = 0;
            if (p.y > h) p.y = h;
            break;
          }
          case 'fireflies': {
            p.vx = (Math.random() - 0.5) * 0.3;
            p.vy = (Math.random() - 0.5) * 0.3;
            p.vy += Math.sin(p.phase * 0.4) * 0.006;
            p.vx *= 0.975;
            p.vy *= 0.975;
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            if (p.x < -20) p.x = w + 20;
            if (p.x > w + 20) p.x = -20;
            if (p.y > h + 20) { p.y = -20; p.x = Math.random() * w; }
            break;
          }
          case 'leaves': {
            p.vx = Math.sin(p.phase * 0.4 + p.wobble) * 0.6 * speedMult;
            p.vy = (0.5 + Math.abs(Math.sin(p.phase * 0.2)) * 0.4) * speedMult;
            p.angle += p.rotationSpeed * dt;
            p.wobble += p.wobbleSpeed * dt;
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            if (p.x < -20) p.x = w + 20;
            if (p.x > w + 20) p.x = -20;
            if (p.y > h + 20) { p.y = -20; p.x = Math.random() * w; }
            break;
          }
          case 'dragonfly': {
            p.vx += (Math.random() - 0.5) * 0.12 * speedMult;
            p.vy += (Math.random() - 0.5) * 0.12 * speedMult;
            p.vx *= 0.94;
            p.vy *= 0.94;
            const maxSpd = 1.8 * speedMult;
            const minSpd = 0.3 * speedMult;
            const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
            if (spd > maxSpd) { p.vx = (p.vx / spd) * maxSpd; p.vy = (p.vy / spd) * maxSpd; }
            if (spd < minSpd && spd > 0.01) { p.vx = (p.vx / spd) * minSpd; p.vy = (p.vy / spd) * minSpd; }
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            if (p.x < 0) { p.x = 0; p.vx = Math.abs(p.vx); }
            if (p.x > w) { p.x = w; p.vx = -Math.abs(p.vx); }
            if (p.y < 0) { p.y = 0; p.vy = Math.abs(p.vy); }
            if (p.y > h) { p.y = h; p.vy = -Math.abs(p.vy); }
            break;
          }
          case 'snowflake': {
            p.vx = Math.sin(p.phase * 0.3 + p.wobble * 0.5) * 0.5 * speedMult;
            p.vy = (0.35 + Math.random() * 0.05) * speedMult;
            p.angle += p.spinSpeed * dt;
            p.wobble += p.wobbleSpeed * dt;
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            if (p.x < -20) p.x = w + 20;
            if (p.x > w + 20) p.x = -20;
            if (p.y > h + 20) { p.y = -20; p.x = Math.random() * w; }
            break;
          }
          case 'bubble': {
            p.vx += Math.sin(p.phase * 0.5) * 0.015 * speedMult;
            p.vy += (Math.random() - 0.5) * 0.01 * speedMult;
            p.vy += Math.sin(p.phase * 0.2) * 0.006;
            p.vx *= 0.98;
            p.vy *= 0.98;
            const targetVy = -(0.35 + Math.sin(p.phase * 0.2) * 0.1) * speedMult;
            p.vy += (targetVy - p.vy) * 0.02;
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            if (p.x < -20) p.x = w + 20;
            if (p.x > w + 20) p.x = -20;
            if (p.y < -p.size * 2) { p.y = h + p.size; p.x = Math.random() * w; }
            break;
          }
          case 'star': {
            p.vx += (Math.random() - 0.5) * 0.005 * speedMult;
            p.vy += (Math.random() - 0.5) * 0.005 * speedMult;
            p.vx *= 0.99;
            p.vy *= 0.99;
            p.angle += p.twinkleSpeed * 0.3 * dt;
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            if (p.x < 0) { p.x = 0; p.vx = Math.abs(p.vx); }
            if (p.x > w) { p.x = w; p.vx = -Math.abs(p.vx); }
            if (p.y < 0) { p.y = 0; p.vy = Math.abs(p.vy); }
            if (p.y > h) { p.y = h; p.vy = -Math.abs(p.vy); }
            break;
          }
        }
      }
    }

    // ─── Draw particles (depth-sorted in formation mode)
    ctx.save();
    const useAdditive = cfg.form === 'fireflies' || cfg.form === 'star' || cfg.form === 'bubble';
    ctx.globalCompositeOperation = useAdditive ? 'lighter' : 'source-over';

    // In formation mode, sort by depth so back particles render first
    let drawOrder: number[];
    if (isFormation && targets3D.length > 0) {
      drawOrder = particles.map((_, i) => i).sort((a, b) => {
        const da = targets3D[a]?.depth ?? 0;
        const db = targets3D[b]?.depth ?? 0;
        return da - db; // back to front
      });
    } else {
      drawOrder = particles.map((_, i) => i);
    }

    for (let pi of drawOrder) {
      const p = particles[pi];
      if (toRemove.has(p.id)) continue;
      ctx.globalAlpha = 1;
      switch (cfg.form) {
        case 'fish':       drawFish(ctx, p, t); break;
        case 'butterfly':  drawButterfly(ctx, p, t); break;
        case 'birds':      drawBird(ctx, p, t); break;
        case 'fireflies':  drawFirefly(ctx, p, t); break;
        case 'leaves':     drawLeaf(ctx, p, t); break;
        case 'dragonfly':  drawDragonfly(ctx, p, t); break;
        case 'snowflake':  drawSnowflake(ctx, p, t); break;
        case 'bubble':     drawBubble(ctx, p, t); break;
        case 'star':       drawStar(ctx, p, t); break;
      }
    }
    ctx.restore();

    // ─── Update & draw dying particles
    const dyingParticles = dyingParticlesRef.current;
    const stillDying: DyingParticle[] = [];
    ctx.save();
    ctx.globalCompositeOperation = useAdditive ? 'lighter' : 'source-over';
    for (const dp of dyingParticles) {
      dp.age += 1 / dp.duration;
      if (dp.age >= 1) continue;

      // Move each piece independently
      for (const piece of dp.pieces) {
        piece.x += piece.vx * dt;
        piece.y += piece.vy * dt;
        piece.vy += 0.018 * dt; // gravity
        piece.vx *= 0.96;
        piece.vy *= 0.96;
        piece.angle += piece.spin * dt;
      }

      drawDyingParticle(ctx, dp);
      stillDying.push(dp);
    }
    ctx.restore();
    dyingParticlesRef.current = stillDying;

    const survived = particles.filter(p => !toRemove.has(p.id));
    const needed = cfg.density - survived.length;
    if (needed > 0) {
      const w2 = canvas.width;
      const h2 = canvas.height;
      for (let i = 0; i < Math.min(needed, 5); i++) {
        const id = `p-${++particleIdCounter.current}`;
        const edges: Array<'left' | 'right' | 'top' | 'bottom' | 'random'> =
          cfg.form === 'fish' ? ['left', 'right'] :
          cfg.form === 'birds' ? ['left', 'right', 'top'] :
          cfg.form === 'leaves' || cfg.form === 'snowflake' ? ['top'] :
          cfg.form === 'bubble' ? ['bottom'] : ['random'];
        const edge = edges[Math.floor(Math.random() * edges.length)];
        survived.push(createParticle(id, w2, h2, cfg.form, palette, cfg.particleSize, edge, cfg.bodyColor));
      }
    }
    particlesRef.current = survived;

    onStatsUpdate({
      fps: fpsRef.current,
      particleCount: survived.length,
      isDestroying: rightDestroyRef.current,
    });

    animFrameRef.current = requestAnimationFrame(animate);
  }, [onStatsUpdate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
    resize();

    particlesRef.current = buildParticles(canvas.width, canvas.height, config);

    window.addEventListener('resize', resize);
    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    particlesRef.current = buildParticles(canvas.width, canvas.height, config);
  }, [config.form, config.density, config.colorPalette, buildParticles]);

  // Real-time size update — no rebuild needed, just rescale existing particles
  useEffect(() => {
    for (const p of particlesRef.current) {
      p.size = p.baseSize * config.particleSize;
    }
  }, [config.particleSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onMouseMove = (e: MouseEvent) => {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        mouseRef.current.x = e.clientX - rect.left;
        mouseRef.current.y = e.clientY - rect.top;
      }
      // Handle left-click drag rotation
      if (leftDragRef.current.active) {
        const dx = e.clientX - leftDragRef.current.lastX;
        const dy = e.clientY - leftDragRef.current.lastY;
        rotationRef.current.ry += dx * 0.008;
        rotationRef.current.rx += dy * 0.008;
        // Clamp X rotation to avoid flipping
        rotationRef.current.rx = Math.max(-Math.PI * 0.48, Math.min(Math.PI * 0.48, rotationRef.current.rx));
        leftDragRef.current.lastX = e.clientX;
        leftDragRef.current.lastY = e.clientY;
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 0) {
        leftDragRef.current.active = true;
        leftDragRef.current.lastX = e.clientX;
        leftDragRef.current.lastY = e.clientY;
      }
      if (e.button === 2) {
        rightDestroyRef.current = true;
      }
    };
    const onMouseUp = (e: MouseEvent) => {
      if (e.button === 0) leftDragRef.current.active = false;
      if (e.button === 2) rightDestroyRef.current = false;
    };
    const onContextMenu = (e: MouseEvent) => { e.preventDefault(); };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          mouseRef.current.x = e.touches[0].clientX - rect.left;
          mouseRef.current.y = e.touches[0].clientY - rect.top;
        }
      }
    };
    const onTouchStart = (e: TouchEvent) => {
      mouseRef.current.down = true;
      if (e.touches.length > 0) {
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          mouseRef.current.x = e.touches[0].clientX - rect.left;
          mouseRef.current.y = e.touches[0].clientY - rect.top;
        }
      }
    };
    const onTouchEnd = () => { mouseRef.current.down = false; };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ cursor: 'default', background: 'transparent' }}
    />
  );
}